import { stringUtils } from "cayo.ui";
import { IJsonForm, Operation, IColumn, IFixedList } from "@cayo/shared/api/types/schema";
import { isNullOrWhiteSpace } from "@cayo/shared/types/typeGuards";
import { hasOwnKey } from "@cayo/shared/utils/typeUtils";
import { COLUMNS_WEIGHT, DEFAULT_DIALOG_TITLE } from "./constants";
import { IResultSchemeItems } from "./types";

const isIgnoredProp = (propName: string) => {
  const IGNORED_PROP_PREFIXES = ["startDate", "endDate", "ruleName"];
  const hasIgnoredPrefix = IGNORED_PROP_PREFIXES.some((prefix) => propName.startsWith(prefix));
  return propName === "id" || hasIgnoredPrefix;
};

const getColumnWeight = (columnName: string): number => {
  if (columnName !== null && columnName.startsWith("[")) {
    return -10000;
  }

  if (hasOwnKey(COLUMNS_WEIGHT, columnName)) {
    return COLUMNS_WEIGHT[columnName];
  }

  return 1;
};

export const buildResultDialogScheme = (
  operationsResult: IResultSchemeItems[]
): IJsonForm & { items: IFixedList[] } => {
  const columns: IColumn[] = [];

  const firstDataObj = operationsResult[0];
  const title = `${firstDataObj?.ruleName ?? ""} ${DEFAULT_DIALOG_TITLE}`;

  if (operationsResult.length > 0) {
    if (!isNullOrWhiteSpace(firstDataObj?.reportUrl) && firstDataObj?.details) {
      delete firstDataObj.details;
    }

    if (hasOwnKey(firstDataObj, "reportUrl") && isNullOrWhiteSpace(firstDataObj.reportUrl)) {
      delete firstDataObj.reportUrl;
    }

    // remove the error column only if not a single operation has an error (legacy behavior)
    const shouldRemoveError = !operationsResult.some(
      (operation) => hasOwnKey(operation, "error") && !isNullOrWhiteSpace(operation.error)
    );

    for (const propName of Object.keys(firstDataObj)) {
      if ((shouldRemoveError && propName === "error") || isIgnoredProp(propName)) {
        continue;
      }

      const column: IColumn = {
        key: propName,
        displayName: stringUtils.toSpaceDelimitedPascalCase(propName),
        fieldName: propName,
        iconOnly: false,
        columnActionsMode: "disabled",
        position: getColumnWeight(propName),
        resizable: true,
      };

      // TODO: add tooltips, change styles on cells and implement openReportUrl functionality

      columns.push(column);
    }

    columns.sort((a: IColumn, b: IColumn) => {
      const wa = a.position ?? 0;
      const wb = b.position ?? 0;

      if (wa !== wb) {
        // higher weight comes first
        return wb - wa;
      }
      // if weights are equal, sort alphabetically
      return a.fieldName.localeCompare(b.fieldName);
    });
  }

  return {
    footer: {
      cancelText: "Close",
      okText: null,
    },
    title,
    alwaysShowFooter: true,
    items: [
      {
        layoutMode: "fixedColumns",
        selectionMode: "none",
        showAdjustColumnsButton: false,
        columns,
        actions: [],
        isStandalone: true,
        showSelectionCounter: false,
        hideLabel: true,
        hideWhenEmpty: true,
        readOnly: true,
        type: "fixed-list",
        name: "results",
        items: operationsResult,
      },
    ],
    display: {
      size: "lg",
      allowMaximize: true,
      operation: Operation.Create,
    },
    type: "json-form",
  };
};
